<template>
  <section class="hero-profile">
    <div class="container hero-profile__inner">
      <!-- Titre + sous-titre -->
      <h2 class="hero-profile__title">
        {{ t('heroProfile.titleLine1') }}<br />
        {{ t('heroProfile.titleLine2') }}
      </h2>
      <p class="hero-profile__subtitle">{{ t('heroProfile.subtitle') }}</p>

      <!-- Label exemple -->
      <p class="hero-profile__label">{{ t('heroProfile.exampleLabel') }}</p>

      <!-- Cards -->
      <div class="hero-profile__cards">
        <!-- Card MBTI — masquée sur mobile -->
        <div class="hero-profile__card hero-profile__card--mbti">
          <div class="hero-profile__card-accent" />
          <p class="hero-profile__card-eyebrow">{{ t('heroProfile.mbti.eyebrow') }}</p>
          <div class="hero-profile__mbti-row">
            <div class="hero-profile__mbti-chip">ENFP</div>
            <p class="hero-profile__mbti-desc">{{ t('heroProfile.mbti.desc') }}</p>
          </div>
        </div>

        <!-- Card Forces -->
        <div class="hero-profile__card hero-profile__card--forces">
          <div class="hero-profile__card-accent" />
          <div class="hero-profile__forces-header">
            <p class="hero-profile__forces-title">{{ t('heroProfile.forces.title') }}</p>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              class="hero-profile__chevron">
              <path
                d="M4 11l5-5 5 5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </div>

          <div class="hero-profile__forces-list">
            <div
              v-for="force in forces"
              :key="force.key"
              class="hero-profile__force-row">
              <!-- Gauge -->
              <div class="hero-profile__gauge">
                <p class="hero-profile__gauge-label">{{ t(force.levelKey) }}</p>
                <svg
                  :viewBox="`0 0 ${GW} ${GH}`"
                  :width="GW"
                  :height="GH">
                  <path
                    :d="arcBg"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    :stroke-width="SW"
                    stroke-linecap="round" />
                  <path
                    :d="arcPath(force.pct)"
                    fill="none"
                    :stroke="`url(#gg-${force.key})`"
                    :stroke-width="SW"
                    stroke-linecap="round" />
                  <defs>
                    <linearGradient
                      :id="`gg-${force.key}`"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0">
                      <stop
                        offset="0%"
                        stop-color="#03a0f1" />
                      <stop
                        offset="100%"
                        stop-color="#3f3fd8" />
                    </linearGradient>
                  </defs>
                </svg>
                <span class="hero-profile__gauge-value">{{ force.pct }}%</span>
              </div>

              <!-- Texte force -->
              <div class="hero-profile__force-text">
                <p class="hero-profile__force-name">{{ t(force.nameKey) }}</p>
                <p class="hero-profile__force-desc">{{ t(force.descKey) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <NuxtLink
        :to="localePath('/register')"
        class="btn btn-primary btn-lg hero-profile__cta">
        {{ t('heroProfile.cta') }}
      </NuxtLink>
    </div>
  </section>
</template>

<script setup>
const { t } = useI18n()
const localePath = useLocalePath()

// Dimensions du SVG gauge
const GW = 80 // largeur viewBox
const GH = 50 // hauteur viewBox
const CX = 40 // centre X
const CY = 46 // centre Y (bas, pour que l'arc soit en haut)
const R = 32 // rayon
const SW = 6 // stroke-width

// Arc de fond fixe (demi-cercle complet de -180° à 0°)
const rad = (a) => (a * Math.PI) / 180
const arcBg = (() => {
  const x1 = CX + R * Math.cos(rad(-180))
  const y1 = CY + R * Math.sin(rad(-180))
  const x2 = CX + R * Math.cos(rad(0))
  const y2 = CY + R * Math.sin(rad(0))
  return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`
})()

function formatSvgCoord(value) {
  return Math.round(value * 10_000) / 10_000
}

// Arc de valeur selon pct
function arcPath(pct) {
  const startAngle = -180
  const angle = startAngle + 180 * (pct / 100)
  const x1 = formatSvgCoord(CX + R * Math.cos(rad(startAngle)))
  const y1 = formatSvgCoord(CY + R * Math.sin(rad(startAngle)))
  const x2 = formatSvgCoord(CX + R * Math.cos(rad(angle)))
  const y2 = formatSvgCoord(CY + R * Math.sin(rad(angle)))
  const large = angle - startAngle > 180 ? 1 : 0
  return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`
}

const forces = [
  {
    key: 'visionary',
    pct: 90,
    levelKey: 'heroProfile.forces.visionary.level',
    nameKey: 'heroProfile.forces.visionary.name',
    descKey: 'heroProfile.forces.visionary.desc',
  },
  {
    key: 'critical',
    pct: 85,
    levelKey: 'heroProfile.forces.critical.level',
    nameKey: 'heroProfile.forces.critical.name',
    descKey: 'heroProfile.forces.critical.desc',
  },
  {
    key: 'determined',
    pct: 75,
    levelKey: 'heroProfile.forces.determined.level',
    nameKey: 'heroProfile.forces.determined.name',
    descKey: 'heroProfile.forces.determined.desc',
  },
]
</script>

<style scoped>
.hero-profile {
  background: #151618;
  padding-block: var(--space-20);
}

.hero-profile__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-6);
}

.hero-profile__title {
  font-size: clamp(1.5rem, 3.5vw, 2.25rem);
  font-weight: 700;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.hero-profile__subtitle {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 560px;
}

.hero-profile__label {
  font-size: var(--text-sm);
  color: var(--text-muted);
  margin-top: var(--space-2);
}

/* Cards container */
.hero-profile__cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: 620px;
}

/* Card générique */
.hero-profile__card {
  position: relative;
  background: #1e1f22;
  border-radius: var(--radius-lg);
  padding: var(--space-5) var(--space-6);
  text-align: left;
  overflow: hidden;
}

/* Accent vertical coloré à droite */
.hero-profile__card-accent {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(180deg, #3f3fd8 0%, #03a0f1 100%);
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
}

/* Card MBTI */
.hero-profile__card-eyebrow {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--space-4);
}

.hero-profile__mbti-row {
  display: flex;
  align-items: center;
  gap: var(--space-5);
}

.hero-profile__mbti-chip {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 800;
  color: var(--accent-blue-lt);
  background: rgba(3, 160, 241, 0.12);
  border: 1.5px solid rgba(3, 160, 241, 0.25);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-4);
  flex-shrink: 0;
  letter-spacing: 0.05em;
}

.hero-profile__mbti-desc {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

/* Card Forces */
.hero-profile__forces-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.hero-profile__forces-title {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text-primary);
}

.hero-profile__chevron {
  color: var(--text-muted);
}

.hero-profile__forces-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* Ligne force */
.hero-profile__force-row {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

/* Gauge */
.hero-profile__gauge {
  position: relative;
  flex-shrink: 0;
  width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-profile__gauge-label {
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-align: right;
  align-self: flex-end;
  margin-bottom: 2px;
  line-height: 1;
}

/* Le % centré sous l'arc, dans l'espace vide du demi-cercle */
.hero-profile__gauge-value {
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  font-size: var(--text-base);
  font-weight: 800;
  color: var(--text-primary);
  white-space: nowrap;
  line-height: 1;
}

/* Texte force */
.hero-profile__force-text {
  flex: 1;
  border-left: 1px solid var(--border-subtle);
  padding-left: var(--space-4);
}

.hero-profile__force-name {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.hero-profile__force-desc {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

/* CTA */
.hero-profile__cta {
  margin-top: var(--space-4);
}

/* Responsive */
@media (max-width: 640px) {
  .hero-profile__card--mbti {
    display: none;
  }

  .hero-profile__title {
    font-size: clamp(1.375rem, 6vw, 1.75rem);
    text-align: left;
  }

  .hero-profile__subtitle,
  .hero-profile__label {
    text-align: left;
  }

  .hero-profile__inner {
    align-items: flex-start;
  }

  .hero-profile__cta {
    align-self: center;
  }
}
</style>
